// R8 — **THE ONE EDGE DRAWER**, moved out of App.tsx so the closet (and any drawer added later) can
// import it instead of growing a second body of the law that makes every drawer on this site obey
// the bottom bar and the escape key (R28/R29). Six drawers render through it; there is exactly one.
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useEscapeKey } from "./useEscapeKey";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav";

export function EdgeDrawer({ open, onClose, label, className, children }: {
  open: boolean;
  onClose: () => void;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  // R82: a SHUT drawer hands the law `undefined` and no listener is attached — the guard that used
  // to make this "a different law wearing the same five lines" is the law's own answer now.
  useEscapeKey(open ? onClose : undefined);
  // N1-7 — THE BOTTOM BAR ALWAYS NAVIGATES. Tapping a tab used to do nothing while a drawer was open:
  // the drawer's scrim swallowed the tap, so the member had to find its X first. The bar now announces
  // the move and every drawer steps out of the way. It lives HERE, on the shared base, so all six
  // drawers obey it and any drawer added later obeys it without being told.
  useEffect(() => {
    if (!open) return;
    const step = () => onClose();
    window.addEventListener(CLOSE_OVERLAYS_EVENT, step);
    return () => window.removeEventListener(CLOSE_OVERLAYS_EVENT, step);
  }, [open, onClose]);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="edge-drawer__scrim" onMouseDown={onClose}>
      <div
        className={className ? `edge-drawer ${className}` : "edge-drawer"}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button className="edge-drawer__close" type="button" onClick={onClose} aria-label={`Close ${label}`}>×</button>
        {children}
      </div>
    </div>,
    document.body,
  );
}
